import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Form } from 'softcat:components/form.tsx';
import { Spinner } from 'softcat:components/spinner.tsx';
import { useTerritory } from 'softcat:hooks/use-territory.ts';
import { ConfigService } from 'softcat:services/config.ts';
import useSWR from 'swr';
import * as z from 'zod';

export const ConfigEntitlements = () => {
    const intl = useIntl();
    const { activeTerritory } = useTerritory();
    const [submitting, setSubmitting] = useState(false);
    const [onEditError, setOnEditError] = useState('');
    const [formValues, setFormValues] = useState({ config: '' });

    const EditEntitlementsSchema = z.object({
        config: z.string().refine(
            (value) => {
                try {
                    JSON.parse(value);

                    return true;
                } catch {
                    return false;
                }
            },
            { message: intl.formatMessage({ id: 'FORM_JSON_VALIDATION' }) },
        ),
    });

    const {
        data: entitlementData,
        error: entitlementDataError,
        isLoading: entitlementDataIsLoading,
    } = useSWR({ key: `${activeTerritory}-ConfigServiceFindAllEntitlements` }, ConfigService.findAllEntitlements);

    useEffect(() => {
        if (entitlementData) {
            setFormValues({
                config: JSON.stringify(entitlementData, undefined, 4),
            });
        }
    }, [entitlementData]);

    const onSubmit = async (values: z.infer<typeof EditEntitlementsSchema>) => {
        setSubmitting(true);
        setOnEditError('');

        if (entitlementData) {
            try {
                await ConfigService.updateAllEntitlements(JSON.parse(values.config));

                setSubmitting(false);
            } catch (error) {
                setSubmitting(false);
                setOnEditError((error as Error).message);
            }
        }
    };

    return (
        <Form<z.infer<typeof EditEntitlementsSchema>>
            defaultValues={formValues}
            mode={'onChange'}
            onSubmit={onSubmit}
            resolver={zodResolver(EditEntitlementsSchema)}
        >
            <div className="space-y-3 px-4">
                <Form.Header
                    description={<FormattedMessage id={'ENTITLEMENTS_PAGE_DESCRIPTION'} />}
                    title={<FormattedMessage id={'ENTITLEMENTS_PAGE_HEADING'} />}
                />

                {entitlementDataError && (
                    <p className="mt-2 text-sm text-gray-700">
                        <FormattedMessage id={'ENTITLEMENTS_PAGE_ERROR_LOADING_DATA'} />
                    </p>
                )}

                {entitlementDataIsLoading && <Spinner />}

                {entitlementData && (
                    <>
                        <Form.Textarea label={<FormattedMessage id={'ENTITLEMENTS_FORM_CONFIG_LABEL'} />} name={'config'} rows={16} />

                        <Form.Footer
                            isSubmitting={submitting}
                            submitError={onEditError && <FormattedMessage id={'ENTITLEMENTS_PAGE_FORM_SUBMIT_ERROR'} values={{ error: onEditError }} />}
                            submitText={<FormattedMessage id={'BUTTON_UPDATE'} />}
                        />
                    </>
                )}
            </div>
        </Form>
    );
};
